/**
 * WLP_Report.tsx
 * 100% migration of WLP_Report.aspx + WLP_Report.aspx.vb + WLP_Report.css + header.css
 *
 * Every control, repeater row, CSS rule, footer logic, tip, and icon is replicated here.
 */

import React, { useEffect, useState } from "react";

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK ?? "";

// ─── Types ────────────────────────────────────────────────────────────────────

interface WLPItem {
  unitName:     string;
  chapterName:  string;
  activityName: string;
  activityIcon: "game_icon" | "lsn_icon" | "test_icon" | string;
}

interface WLPData {
  // Student info
  studentName:  string;
  weekEnding:   string;
  grade:        string;
  weekNumber:   string;   // e.g. "Week 3"

  // Heading
  pageTitle:    string;
  reportHeading: string;

  // Tip of the week
  tipLine1: string;
  tipLine2: string;

  // Logo
  logoUrl: string;

  // Footer
  footerInfo:         string;
  footerUrl:          string;
  footerText:         string;
  footerImageVisible: boolean;
  footerPhoneImgUrl:  string;

  // Table rows — mirrors asp:Repeater rptWLPReport DataSource=dtWLPDetails
  items: WLPItem[];

  // Misc
  studyYear: number;
  subject:   string;
  country:   string;
}

// ─── Inline styles (mirrors WLP_Report.css + header.css exactly) ─────────────

const css = `
  html, body {
    font-family: Arial, 'Arimo', sans-serif;
    padding: 0;
    margin: 0;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  /*** HEADER ***/
  .header_container {
    width: 100vw;
    height: 40px;
    background-color: #333;
    display: block;
    padding: 0px;
    text-align: center;
    color: #ffffff;
    margin-bottom: 40px;
  }

  /*** GENERAL STYLES ***/
  .section_div {
    margin: 0 auto;
    height: auto;
    width: 80%;
    display: block;
    clear: both;
    padding: 0;
    overflow: hidden;
  }

  .report_heading {
    font-size: 24px;
    font-weight: bold;
  }

  /*** STUDENT INFO ***/
  .student_info_container {
    display: inline-block;
    font-size: 18px;
  }

  .student_info_labels {
    display: block;
    float: left;
    margin-bottom: 5px;
    margin-top: 5px;
    font-weight: bold;
  }

  .student_info_content {
    display: block;
    float: left;
    margin-bottom: 5px;
    margin-top: 5px;
    margin-left: 15px;
  }

  .section_text {
    margin: 0;
    padding: 0;
  }

  .advice {
    display: inline-block;
    float: right;
    width: 500px;
    background: #bad1e3;
    padding: 20px;
    box-sizing: border-box;
  }

  .advice p {
    font-weight: bold;
    margin: 0;
    padding: 0;
  }

  .advice span {
    font-style: italic;
  }

  /*** TABLE LEGEND ***/
  .legend_container {
    margin-top: 20px;
  }

  .legend_item {
    display: inline-block;
    margin-right: 15px;
  }

  .legend_icons {
    display: inline-block;
    vertical-align: middle;
  }

  .legend_text {
    display: inline-block;
    font-weight: bold;
    color: #0f5f9a;
  }

  .game_icon {
    background-image: url('/Image/games_icon.png');
    width: 28px;
    height: 19px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
  }

  .lsn_icon {
    background-image: url('/Image/lessons_icon.png');
    width: 31px;
    height: 32px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
  }

  .test_icon {
    background-image: url('/Image/tests_icon.png');
    width: 28px;
    height: 28px;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
  }

  /*** WLP TABLE ***/
  .wlp_table {
    width: 100%;
    display: table;
    margin-top: 15px;
    margin-bottom: 40px;
    box-sizing: border-box;
    border: 5px solid #ffcca3;
    border-collapse: collapse;
  }

  .colspan {
    display: table-caption;
    background-color: #ffcca3;
    border-top: 5px solid #ffcca3;
    border-left: 5px solid #ffcca3;
    border-right: 5px solid #ffcca3;
    border-bottom: 0;
    padding: 25px;
  }

  .table_head_left {
    width: 20%;
    display: inline-block;
    vertical-align: middle;
  }

  .table_head_right {
    width: 78%;
    display: inline-block;
    vertical-align: middle;
  }

  .week_counter {
    font-size: 32px;
    font-weight: bold;
  }

  .move_right {
    float: right;
  }

  .tip_icon {
    display: inline-block;
    background-image: url('/Image/mathswiz.png');
    width: 125px;
    height: 90px;
    background-size: contain;
    background-repeat: no-repeat;
    vertical-align: middle;
  }

  .tip_text {
    display: inline-block;
    width: 600px;
    vertical-align: middle;
    margin-left: 15px;
    font-size: 18px;
    font-style: italic;
    line-height: 24px;
  }

  .tip_text p {
    font-size: 21px;
    line-height: 24px;
    font-weight: bold;
    text-decoration: underline;
    margin: 0;
    padding: 2px;
  }

  .tr {
    display: table-row;
    vertical-align: middle;
  }

  .td {
    display: table-cell;
    padding: 25px;
    vertical-align: middle;
  }

  .colhead {
    font-size: 18px;
    font-weight: bold;
    background: #ffe5d0;
    color: #0f5f9a;
    border-bottom: 5px solid #ffcca3;
  }

  .col1 {
    width: 15%;
    border-right: 5px solid #ffcca3;
  }

  .col2 {
    width: 25%;
    border-right: 5px solid #ffcca3;
  }

  .col3 {
    width: 60%;
  }

  .activity_block {
    margin-bottom: 15px;
  }

  .activity_block:last-child {
    margin-bottom: 0;
  }

  .activity_icon {
    display: inline-block;
    vertical-align: middle;
  }

  .activity_text {
    display: inline-block;
  }

  /*** FOOTER ***/
  .footer {
    width: 100vw;
    height: auto;
    background-color: #46a8bf;
    display: block;
    box-sizing: border-box;
    padding: 35px 15px 25px 15px;
    text-align: center;
    color: #ffffff;
    position: relative;
    left: 0;
    bottom: 0;
  }

  .footer p {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  .footer_call_info {
    font-size: 18px;
    font-weight: bold;
    box-sizing: border-box;
  }

  .footer_call_info a {
    text-decoration: underline;
  }

  a {
    font-size: 18px;
    font-style: italic;
    color: #ffffff;
  }

  /*** HEADER scrolled logo — from header.css ***/
  .scrolled_logo {
    height: 40px;
    width: 135px;
    display: block;
    margin-right: 10px;
    float: left;
    padding: 0;
    background-size: contain;
    background-repeat: no-repeat;
    cursor: default;
  }

  /*** LOADING ***/
  .wlp-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    font-family: Arial, sans-serif;
    font-size: 16px;
    color: #555;
  }

  .wlp-error {
    padding: 40px;
    text-align: center;
    font-family: Arial, sans-serif;
    font-size: 18px;
    color: #c00;
  }

  /*** RESPONSIVE — mirrors WLP_Report.css media queries ***/

  @media screen and (max-width: 1268px) {
    .advice {
      width: 100%;
      float: none;
      clear: both;
    }
    .table_head_left {
      width: 100%;
      display: block;
      text-align: center;
    }
    .table_head_right {
      width: 100%;
      display: block;
    }
    .move_right {
      float: none;
      clear: both;
    }
    .tip_text {
      width: 80%;
    }
  }

  @media screen and (max-width: 800px) {
    .footer {
      padding: 10px 8px 10px 8px;
    }
  }

  @media screen and (max-width: 740px) {
    .section_div {
      width: 90%;
    }
    .print_btn {
      margin: 22px auto 22px;
      clear: both;
      float: none;
      display: block;
    }
    .tip_icon {
      display: block;
      margin: 0 auto;
      text-align: center;
      background-position: center;
    }
    .td {
      padding: 15px;
    }
    .colhead {
      padding: 15px;
    }
  }

  @media screen and (max-width: 800px) {
    .header_container {
      height: 70px;
    }
    .scrolled_logo {
      height: 70px;
      background-position: center;
    }
  }

  @media screen and (max-width: 720px) {
    .header_container {
      height: 40px;
    }
    .scrolled_logo {
      height: 40px;
      width: 100px;
    }
  }
`;

// ─── Activity icon component ───────────────────────────────────────────────
// mirrors: <div class='activity_icon <%# Eval("ActivityIcon")%>'></div>
function ActivityIcon({ iconClass }: { iconClass: string }) {
  return <div className={`activity_icon ${iconClass}`} />;
}

// ─── Loading overlay ───────────────────────────────────────────────────────
function LoadingSpinner() {
  return (
    <div className="wlp-loading">
      <div style={{ textAlign: "center" }}>
        <div style={{
          width: 44, height: 44, margin: "0 auto 14px",
          border: "4px solid #ddd", borderTopColor: "#46a8bf",
          borderRadius: "50%", animation: "wlp-spin 0.8s linear infinite",
        }} />
        Loading report…
        <style>{`@keyframes wlp-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
export default function WLP_Report() {

  // ── Read query string — mirrors Request.QueryString in Page_Load ──────────
  const qp          = new URLSearchParams(window.location.search);
  const stuSch      = qp.get("StuSch")       ?? "";
  const stuFam      = qp.get("StuFam")       ?? "";
  const studentId   = qp.get("StudentId")    ?? "";
  const firstName   = qp.get("FirstName")    ?? "";
  const countryQS   = qp.get("Country")      ?? "";
  const subjectQS   = qp.get("Subject")      ?? "";
  const weekendDate = qp.get("WeekendDate")  ?? "";
  const planCreated = qp.get("PlanCreatedOn") ?? "";

  // ── Data state ────────────────────────────────────────────────────────────
  const [data,    setData]    = useState<WLPData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  // ── Fetch on mount — mirrors Page_Load IsPostBack=False ──────────────────
  useEffect(() => {
    if (!stuSch || !stuFam || !studentId || !firstName || !countryQS || !subjectQS) {
      setError("Missing required parameters.");
      setLoading(false);
      return;
    }

    const params = new URLSearchParams({
      StuSch:       stuSch,
      StuFam:       stuFam,
      StudentId:    studentId,
      FirstName:    firstName,
      Country:      countryQS,
      Subject:      subjectQS,
      WeekendDate:  weekendDate,
      PlanCreatedOn: planCreated,
    });

    fetch(`${API_BASE_URL}api/wlp-report/?${params.toString()}`)
      .then((r) => r.json())
      .then((d: WLPData & { error?: string }) => {
        if (d.error) {
          setError(d.error);
        } else {
          setData(d);
          // mirrors: PageTitle.InnerText = ...
          document.title = d.pageTitle;
        }
      })
      .catch(() => setError("Failed to load report. Please try again."))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return (
    <>
      <style>{css}</style>
      <LoadingSpinner />
    </>
  );

  if (error) return (
    <>
      <style>{css}</style>
      <div className="wlp-error">{error}</div>
    </>
  );

  if (!data) return null;

  return (
    <>
      <style>{css}</style>

      {/* ── HEADER — mirrors <div class="header_container"> + divLogo ── */}
      <div className="header_container">
        <div className="section_div">
          {/* mirrors: <div id="divLogo" class="scrolled_logo" runat="server">
              with divLogo.Style.Add("background-image", ...) */}
          <div
            className="scrolled_logo"
            style={{
              backgroundImage: data.logoUrl ? `url('${data.logoUrl}')` : undefined,
            }}
          />
        </div>
      </div>

      {/* ── STUDENT INFO SECTION ── */}
      <div className="section_div">

        {/* mirrors: <div class="report_heading"><span id="ReportHeading" runat="server"> */}
        <div className="report_heading">
          <span id="ReportHeading">{data.reportHeading}</span>
        </div>

        {/* mirrors: STUDENT INFORMATION block */}
        <div className="student_info_container">
          <div className="student_info_labels">
            <p className="section_text">
              Student Name: <br />
              Week Ending: <br />
              Grade/Year: <br />
            </p>
          </div>
          <div className="student_info_content">
            <p className="section_text">
              {/* mirrors: <asp:Label ID="lblStudentName" /> */}
              <span id="lblStudentName">{data.studentName}</span><br />
              {/* mirrors: <asp:Label ID="lblWeekEnding" /> */}
              <span id="lblWeekEnding">{data.weekEnding}</span><br />
              {/* mirrors: <asp:Label ID="lblGrade" /> */}
              <span id="lblGrade">{data.grade}</span><br />
            </p>
          </div>
        </div>

        {/* mirrors: <div class="advice"> */}
        <div className="advice">
          <p>IMPORTANT!</p><br />
          <span>
            For best results, it is recommended that you also practice what you are doing in school today.
          </span>
        </div>

        {/* PRINT BUTTON — commented out in original, mirrors the commented-out block */}
        {/* <div className="print_btn"><p>PRINT REPORT</p></div> */}
      </div>

      {/* ── WLP TABLE SECTION ── */}
      <div className="section_div">

        {/* mirrors: Legend for report */}
        <div className="legend_container">
          <div className="legend_item">
            <div className="legend_icons game_icon" />
            <div className="legend_text">Game</div>
          </div>
          <div className="legend_item">
            <div className="legend_icons lsn_icon" />
            <div className="legend_text">Lesson</div>
          </div>
          <div className="legend_item">
            <div className="legend_icons test_icon" />
            <div className="legend_text">Test</div>
          </div>
        </div>

        {/* mirrors: <div class="wlp_table"> */}
        <div className="wlp_table">

          {/* mirrors: <div class="colspan"> ... table_head_left + table_head_right */}
          <div className="colspan">
            <div className="table_head_left">
              <div className="week_counter">
                {/* mirrors: <asp:Label ID="lblWeekNumber" /> */}
                <span id="lblWeekNumber">{data.weekNumber}</span>
              </div>
            </div>
            <div className="table_head_right">
              <div className="move_right">
                {/* mirrors: tip_icon + tip_text */}
                <div className="tip_icon" />
                <div className="tip_text">
                  <p>Tip of the week:</p>
                  {/* mirrors: <asp:Label ID="lblTip" /> with HTML content
                      lblTip.Text = arrTips(WeekNo Mod 12) & " <br />" & arrTips((WeekNo Mod 12) + 12) */}
                  <span id="lblTip">
                    {data.tipLine1}
                    {data.tipLine2 && (
                      <>
                        <br />
                        {data.tipLine2}
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* mirrors: column headers <div class="tr"> */}
          <div className="tr">
            <div className="td col1 colhead">Unit</div>
            <div className="td col2 colhead">Chapter</div>
            <div className="td col3 colhead">Activity</div>
          </div>

          {/* mirrors: <asp:Repeater ID="rptWLPReport" runat="server">
              <ItemTemplate> ... </ItemTemplate> </asp:Repeater>
              DataSource = dtWLPDetails (rows with UnitName / ChapterName / ActivityName / ActivityIcon) */}
          {data.items.map((item, idx) => (
            <div className="tr" key={idx}>
              {/* mirrors: <div class="td col1"><span id="UnitName"><%# Eval("UnitName") %></span></div> */}
              <div className="td col1">
                <span id={`UnitName_${idx}`}>{item.unitName}</span>
              </div>
              {/* mirrors: <div class="td col2"><span id="ChapterName"><%# Eval("ChapterName") %></span></div> */}
              <div className="td col2">
                <span id={`ChapterName_${idx}`}>{item.chapterName}</span>
              </div>
              {/* mirrors: <div class="td col3"> activity_block with icon + text */}
              <div className="td col3">
                <div className="activity_block">
                  {/* mirrors: <div class='activity_icon <%# Eval("ActivityIcon")%>'></div> */}
                  <ActivityIcon iconClass={item.activityIcon} />
                  {/* mirrors: <div class="activity_text"><span id="ActivityName"><%# Eval("ActivityName") %></span></div> */}
                  <div className="activity_text">
                    <span id={`ActivityName_${idx}`}>{item.activityName}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FOOTER ── */}
      {/* mirrors: <div class="footer"> ... </div> */}
      <div className="footer">
        {/* mirrors: <p class="footer_call_info">
              <asp:Image ImageUrl="~/MD_Reports/Image/fr_phone.png" ID="Footerimg" Visible="false" />
              <asp:Label ID="lblInfo" runat="server"></asp:Label>
            </p> */}
        <p className="footer_call_info">
          {data.footerImageVisible && data.footerPhoneImgUrl && (
            <img
              src={data.footerPhoneImgUrl}
              alt="phone"
              style={{ verticalAlign: "middle", marginRight: 8 }}
            />
          )}
          {/* mirrors: lblInfo.Text = "For tutor assistance call ..." */}
          <span id="lblInfo">{data.footerInfo}</span>
        </p>

        {/* mirrors: <p class="footer_website" id="footerLink" runat="server">
              <asp:HyperLink ID="SetSite" runat="server" target="_blank"></asp:HyperLink>
            </p> */}
        {(data.footerUrl || data.footerText) && (
          <p className="footer_website" id="footerLink">
            <a
              href={data.footerUrl}
              target="_blank"
              rel="noopener noreferrer"
              id="SetSite"
            >
              {data.footerText}
            </a>
          </p>
        )}
      </div>
    </>
  );
}